import { AnyAction, ThunkAction } from '@reduxjs/toolkit';
import { AppState } from 'src/redux/types';
import { assertNever } from 'src/utils/assert';
import { socketClientSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { annotationActions } from '../actions';
import { createLocalAnnotationCommandByCommandIdSelector } from '../selectors';
import { Annotation } from 'src/models/annotation';

/** A thunk action which syncs a single annotation command by its command id. */
export const syncAnnotationCommand =
  (commandId: string): ThunkAction<Promise<void>, AppState, undefined, AnyAction> =>
  async (dispatch, getState) => {
    if (!socketClientSentinel.socket.connected) return;

    try {
      dispatch(annotationActions.annotationSyncCommandStart({ commandId }));
      const commandSelector = createLocalAnnotationCommandByCommandIdSelector(commandId);
      const command = commandSelector(getState());
      if (!command) throw new Error('No command for the provided id found');

      let result: Annotation;
      switch (command.type) {
        case 'create': {
          if (!command.parentId) throw new Error('Cannot create new annotation without a parentId');

          const data = {
            _id: command.annotationId,
            parentId: command.parentId,
            timestamp: command.timestamp,
            comment: command.comment,
          };
          result = await socketClientSentinel.request('annotations create', data);
          break;
        }
        case 'update': {
          const data = {
            _id: command.annotationId,
            update: { $set: { comment: command.comment } },
          };
          result = await socketClientSentinel.request('annotations update', data);
          break;
        }
        case 'remove': {
          const data = { _id: command.annotationId };
          result = await socketClientSentinel.request('annotations delete', data);
          break;
        }
        case 'restore': {
          const data = { _id: command.annotationId, update: { $set: { deletedOn: null } } };
          result = await socketClientSentinel.request('annotations update', data);
          break;
        }
        default:
          assertNever(command);
      }
      dispatch(annotationActions.annotationSyncCommandSuccess({ commandId, data: result, type: command.type }));
    } catch (e) {
      dispatch(annotationActions.annotationSyncCommandFailure({ commandId }));
      throw e;
    }
  };
